/**
 * useJobStream — three-layer streaming fallback for job progress.
 * Architecture v1.1 §11 / ADR-007:
 *   Layer 1: WS  /api/v1/jobs/{jobId}/ws?token=<jwt>
 *   Layer 2: SSE /api/v1/jobs/{jobId}/events?token=<jwt>
 *   Layer 3: Long-poll GET /api/v1/jobs/{jobId} every 5 s
 * Auto-reconnects on disconnect (max 3 retries, exponential backoff).
 *
 * Public types + helpers live in useJobStream.types.ts (file-size cap).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  API_BASE,
  MAX_RETRIES,
  POLL_INTERVAL_MS,
  RETRY_BASE_MS,
  getToken,
  type JobEvent,
  type JobStreamState,
} from './useJobStream.types';

export type { JobEvent, JobStreamState } from './useJobStream.types';

export function useJobStream(jobId: string): JobStreamState {
  const [state, setState] = useState<JobStreamState>({
    status: null,
    progress: 0,
    events: [],
    isConnected: false,
    error: null,
    layer: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retriesRef = useRef(0);
  const mountedRef = useRef(true);

  const addEvent = useCallback((ev: JobEvent) => {
    setState((prev) => ({
      ...prev,
      progress: ev.progress_pct,
      events: [...prev.events, ev],
    }));
  }, []);

  const stopAll = useCallback(() => {
    wsRef.current?.close(); wsRef.current = null;
    sseRef.current?.close(); sseRef.current = null;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // --- Layer 3: Long-poll ---
  const startPoll = useCallback(() => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, isConnected: true, layer: 'poll', error: null }));
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const token = await getToken();
          const resp = await fetch(`${API_BASE}/api/v1/jobs/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) return;
          const data = (await resp.json()) as { status?: string; decision?: string };
          if (!mountedRef.current) return;
          setState((prev) => ({ ...prev, status: data.status ?? prev.status }));
          if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
            const interval = pollRef.current;
            if (interval) clearInterval(interval);
            pollRef.current = null;
            setState((prev) => ({ ...prev, isConnected: false }));
          }
        } catch {
          // Network error — keep polling
        }
      })();
    }, POLL_INTERVAL_MS);
  }, [jobId]);

  // --- Layer 2: SSE ---
  const startSSE = useCallback(() => {
    if (!mountedRef.current) return;
    void (async () => {
      const token = await getToken();
      if (!mountedRef.current) return;
      const url = `${API_BASE}/api/v1/jobs/${jobId}/events?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      sseRef.current = es;
      setState((prev) => ({ ...prev, isConnected: true, layer: 'sse', error: null }));

      es.onmessage = (e: MessageEvent) => {
        if (!mountedRef.current) return;
        try {
          const ev = JSON.parse(e.data as string) as JobEvent;
          addEvent(ev);
          if (ev.event_type === 'pipeline_completed' || ev.event_type === 'pipeline_failed') {
            es.close();
            setState((prev) => ({ ...prev, isConnected: false, status: ev.event_type === 'pipeline_completed' ? 'completed' : 'failed' }));
          }
        } catch { /* ignore parse errors */ }
      };

      es.onerror = () => {
        es.close();
        sseRef.current = null;
        if (!mountedRef.current) return;
        retriesRef.current += 1;
        if (retriesRef.current <= MAX_RETRIES) {
          setTimeout(startPoll, RETRY_BASE_MS * retriesRef.current);
        } else {
          // SSE exhausted — polling is the terminal fallback layer.
          startPoll();
        }
      };
    })();
  }, [jobId, addEvent, startPoll]);

  // --- Layer 1: WebSocket ---
  const startWS = useCallback(() => {
    if (!mountedRef.current) return;
    void (async () => {
      const token = await getToken();
      if (!mountedRef.current) return;
      const wsBase = API_BASE.replace(/^http/, 'ws');
      const url = `${wsBase}/api/v1/jobs/${jobId}/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        retriesRef.current = 0;
        setState((prev) => ({ ...prev, isConnected: true, layer: 'ws', error: null }));
      };

      ws.onmessage = (e: MessageEvent) => {
        if (!mountedRef.current) return;
        try {
          const ev = JSON.parse(e.data as string) as JobEvent;
          if (ev.event_type === 'heartbeat') return;
          addEvent(ev);
          if (ev.event_type === 'pipeline_completed' || ev.event_type === 'pipeline_failed') {
            ws.close();
            setState((prev) => ({ ...prev, isConnected: false, status: ev.event_type === 'pipeline_completed' ? 'completed' : 'failed' }));
          }
        } catch { /* ignore parse errors */ }
      };

      ws.onerror = () => {
        ws.close();
        wsRef.current = null;
        if (!mountedRef.current) return;
        // WebSocket failed — fall back to SSE
        startSSE();
      };

      ws.onclose = (e: CloseEvent) => {
        if (!mountedRef.current) return;
        if (e.code === 4001 || e.code === 4004) {
          setState((prev) => ({ ...prev, isConnected: false, error: 'Authentication or access error' }));
          return;
        }
        // Unexpected close — fall back to SSE
        retriesRef.current += 1;
        if (retriesRef.current <= MAX_RETRIES) {
          setTimeout(startSSE, RETRY_BASE_MS * retriesRef.current);
        } else {
          startPoll();
        }
      };
    })();
  }, [jobId, addEvent, startSSE, startPoll]);

  useEffect(() => {
    mountedRef.current = true;
    retriesRef.current = 0;
    startWS();

    return () => {
      mountedRef.current = false;
      stopAll();
    };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}
